/**
 * Tervezet (Beosztás-szándék) — dolgozói oldal.
 *
 * - A dolgozó kiválaszt egy jövőbeli napot, majd egyet az engedélyezett
 *   sablonok közül.
 * - Mindenhol látható figyelmeztetés: ez NEM a végleges beosztás.
 * - A meglévő tervezetek itt is listázva (pending / approved / rejected).
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Info, Check, Trash2, Clock, MapPin, CalendarDays, Plus, Moon } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { api, formatApiError } from "@/lib/api";
import { DraftPickerDrawer } from "./DraftPickerDrawer";
import { useLocalizedDates } from "./i18n/dates";
import { localizeTemplateName } from "./i18n/shiftNames";

// Sablon szín → Tailwind class-ek
const COLOR_DOT = {
  indigo: "bg-indigo-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  rose: "bg-rose-500",
  sky: "bg-sky-500",
  violet: "bg-violet-500",
  teal: "bg-teal-500",
  fuchsia: "bg-fuchsia-500",
  orange: "bg-orange-500",
  slate: "bg-slate-500",
};
const COLOR_CHIP = {
  indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:ring-indigo-900/40",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/40",
  amber: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900/40",
  rose: "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900/40",
  sky: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900/40",
  violet: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-900/40",
  teal: "bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:ring-teal-900/40",
  fuchsia: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-950/40 dark:text-fuchsia-300 dark:ring-fuchsia-900/40",
  orange: "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:ring-orange-900/40",
  slate: "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:ring-slate-700",
};

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtIso = (iso, days, months) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return `${months[d.getMonth()]} ${d.getDate()}. (${days[d.getDay()].slice(0, 3)}.)`;
};

const STATUS_TONE = {
  pending: "amber",
  approved: "emerald",
  rejected: "rose",
};

const TONE_CLASS = {
  amber: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  rose: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
};

export default function PortalDrafts() {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState([]);
  const [allowed, setAllowed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerDate, setPickerDate] = useState(null);

  const load = async () => {
    try {
      const [dRes, tRes] = await Promise.all([
        api.get("/portal/shift-drafts"),
        api.get("/portal/shift-drafts/allowed-templates"),
      ]);
      setDrafts(dRes.data?.items || []);
      setAllowed(tRes.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const todayIso = toIso(new Date());
  const draftsByDate = useMemo(() => new Map(drafts.map((d) => [d.date, d])), [drafts]);

  const pendingCount = drafts.filter((d) => d.status === "pending").length;

  const openPicker = (iso) => {
    setPickerDate(iso);
    setPickerOpen(true);
  };

  const onDeleteDraft = async (id) => {
    try {
      await api.delete(`/portal/shift-drafts/${id}`);
      toast.success(t("drafts.deleted_toast"));
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("common.error"));
    }
  };

  return (
    <div className="space-y-6 px-4 pb-8 pt-6 md:px-6 md:pt-8">
      {/* Header */}
      <header>
        <p className="text-[14px] text-zinc-500">{t("drafts.page_header")}</p>
        <h1 className="mt-1 text-[28px] font-semibold tracking-tight md:text-[32px]">
          {t("drafts.page_title")}
        </h1>
        <p className="mt-1.5 text-[13px] text-zinc-500">
          {allowed.length > 0
            ? `${t("drafts.selectable_count", { count: allowed.length })} · ${t("drafts.pending_count", { count: pendingCount })}`
            : t("drafts.no_allowed")}
        </p>
      </header>

      {/* Warning banner */}
      <div
        data-testid="drafts-warning"
        className="flex items-start gap-3 rounded-2xl bg-amber-50 px-4 py-3 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:ring-amber-900/40"
      >
        <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
          <Info className="h-3.5 w-3.5" strokeWidth={2.2} />
        </div>
        <div className="text-[12.5px] leading-snug text-amber-800 dark:text-amber-200">
          <p className="font-semibold">{t("drafts.not_final_title")}</p>
          <p className="mt-0.5 text-amber-700 dark:text-amber-300">
            {t("drafts.not_final_body")}
          </p>
        </div>
      </div>

      {/* Templates legend — color chips */}
      {allowed.length > 0 && (
        <TemplatesLegend templates={allowed} />
      )}

      {/* No allowed templates yet */}
      {!loading && allowed.length === 0 && (
        <div className="rounded-2xl bg-white p-10 text-center shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
          <p className="text-[14px] font-semibold tracking-tight">
            {t("drafts.no_templates_title")}
          </p>
          <p className="mt-1.5 text-[12.5px] text-zinc-500">
            {t("drafts.no_templates_body")}
          </p>
        </div>
      )}

      {/* Calendar pick */}
      {allowed.length > 0 && (
        <DraftCalendar
          drafts={draftsByDate}
          todayIso={todayIso}
          onPick={openPicker}
        />
      )}

      {/* Existing drafts list */}
      <section>
        <div className="mb-3 flex items-center justify-between px-1">
          <h2 className="text-[18px] font-semibold tracking-tight">{t("drafts.my_drafts")}</h2>
          <span className="text-[12px] text-zinc-400">{t("drafts.items_count", { count: drafts.length })}</span>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900" />
            ))}
          </div>
        ) : drafts.length === 0 ? (
          <div className="rounded-2xl bg-white p-10 text-center shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
            <div className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400">
              <CalendarDays className="h-4 w-4" strokeWidth={2} />
            </div>
            <p className="mt-3 text-[13.5px] text-zinc-500">
              {t("drafts.no_drafts_title")}
            </p>
            <p className="mt-1 text-[12px] text-zinc-400">
              {t("drafts.no_drafts_body")}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {drafts.map((d) => (
              <DraftRow
                key={d.id}
                draft={d}
                onDelete={() => onDeleteDraft(d.id)}
              />
            ))}
          </div>
        )}
      </section>

      <DraftPickerDrawer
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        date={pickerDate}
        templates={allowed}
        existingDraft={pickerDate ? draftsByDate.get(pickerDate) : null}
        onSaved={() => {
          setPickerOpen(false);
          load();
        }}
      />
    </div>
  );
}

function DraftCalendar({ drafts, todayIso, onPick }) {
  const { t } = useTranslation();
  const { months, dateFnsLocale } = useLocalizedDates();
  const [month, setMonth] = useState(new Date());

  // Per-color date groups → külön CSS modifier mindegyikre
  const colorGroups = useMemo(() => {
    const map = {};
    Array.from(drafts.values()).forEach((d) => {
      const c = d.is_day_off ? "slate" : (d.color || "indigo");
      if (!map[c]) map[c] = [];
      map[c].push(new Date(d.date + "T00:00:00"));
    });
    return map;
  }, [drafts]);

  const modifiers = useMemo(() => {
    const out = {};
    Object.entries(colorGroups).forEach(([color, dates]) => {
      out[`draft_${color}`] = dates;
    });
    return out;
  }, [colorGroups]);

  const modifiersClassNames = useMemo(() => {
    const DOT = {
      indigo: "after:bg-indigo-500",
      emerald: "after:bg-emerald-500",
      amber: "after:bg-amber-500",
      rose: "after:bg-rose-500",
      sky: "after:bg-sky-500",
      violet: "after:bg-violet-500",
      teal: "after:bg-teal-500",
      fuchsia: "after:bg-fuchsia-500",
      orange: "after:bg-orange-500",
      slate: "after:bg-slate-500",
    };
    const out = {};
    Object.keys(colorGroups).forEach((color) => {
      out[`draft_${color}`] =
        "relative font-semibold after:content-[''] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:h-1.5 after:w-1.5 after:rounded-full " +
        (DOT[color] || DOT.indigo);
    });
    return out;
  }, [colorGroups]);

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
      <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-3 dark:border-zinc-800">
        <p className="text-[14px] font-semibold tracking-tight">
          {month.getFullYear()}. {months[month.getMonth()]}
        </p>
        <div className="flex items-center rounded-full bg-zinc-100 p-1 dark:bg-zinc-800/60">
          <button
            type="button"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            data-testid="drafts-cal-prev"
            className="grid h-7 w-7 place-items-center rounded-full text-zinc-500 transition hover:bg-white hover:text-zinc-900 dark:hover:bg-zinc-700"
            aria-label={t("common.back")}
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => setMonth(new Date())}
            className="px-2 text-[12px] font-medium text-zinc-700 transition hover:text-blue-600 dark:text-zinc-300"
          >
            {t("common.today")}
          </button>
          <button
            type="button"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            data-testid="drafts-cal-next"
            className="grid h-7 w-7 place-items-center rounded-full text-zinc-500 transition hover:bg-white hover:text-zinc-900 dark:hover:bg-zinc-700"
            aria-label={t("common.next")}
          >
            <ChevronRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
      <div className="px-3 pb-3 pt-2">
        <Calendar
          mode="single"
          locale={dateFnsLocale}
          weekStartsOn={1}
          month={month}
          onMonthChange={setMonth}
          disabled={(d) => toIso(d) <= todayIso}
          onDayClick={(d) => {
            const iso = toIso(d);
            if (iso <= todayIso) return;
            onPick(iso);
          }}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          classNames={{
            months: "flex flex-col gap-3",
            caption: "hidden", // we render our own header
            table: "w-full border-collapse",
            head_row: "flex w-full justify-between",
            head_cell: "w-10 text-center text-[10.5px] font-medium uppercase text-zinc-400",
            row: "flex w-full justify-between mt-1",
            cell: "w-10 h-9 text-center p-0",
            day: "h-9 w-9 inline-flex items-center justify-center rounded-full text-[13px] font-medium tabular-nums text-zinc-700 hover:bg-zinc-100 focus:outline-none dark:text-zinc-200 dark:hover:bg-zinc-800",
            day_today: "ring-2 ring-blue-500 ring-inset",
            day_outside: "text-zinc-300 dark:text-zinc-700",
            day_disabled: "text-zinc-300 cursor-not-allowed hover:bg-transparent dark:text-zinc-700",
            day_hidden: "invisible",
          }}
        />
      </div>
    </div>
  );
}

function DraftRow({ draft, onDelete }) {
  const { t } = useTranslation();
  const { days, months } = useLocalizedDates();
  const STATUS_LABEL = {
    pending: t("drafts.status_pending"),
    approved: t("drafts.status_approved"),
    rejected: t("drafts.status_rejected"),
  };
  const meta = { label: STATUS_LABEL[draft.status] || STATUS_LABEL.pending, tone: STATUS_TONE[draft.status] || "amber" };
  const canDelete = draft.status === "pending";
  const dotColor = draft.is_day_off ? "slate" : (draft.color || "indigo");
  return (
    <div
      data-testid={`draft-${draft.id}`}
      className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900"
    >
      <span
        className={"h-9 w-1 shrink-0 rounded-full " + (COLOR_DOT[dotColor] || COLOR_DOT.indigo)}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-[14.5px] font-semibold tracking-tight">{fmtIso(draft.date, days, months)}</p>
          <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium " + TONE_CLASS[meta.tone]}>
            {meta.label}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-zinc-500">
          {draft.is_day_off ? (
            <span className="inline-flex items-center gap-1 font-medium">
              <Moon className="h-3 w-3" strokeWidth={2} />
              {t("drafts.day_off")}
            </span>
          ) : (
            <>
              <span className="font-mono tabular-nums">{draft.start_time}–{draft.end_time}</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" strokeWidth={2} />
                {Number(draft.duration_hours || 0).toFixed(1)}h
              </span>
            </>
          )}
          {draft.site_name && !draft.is_day_off && (
            <span className="inline-flex items-center gap-1 truncate">
              <MapPin className="h-3 w-3" strokeWidth={2} />
              {draft.site_name}
            </span>
          )}
        </div>
        {draft.template_name && !draft.is_day_off && (
          <p className="mt-0.5 truncate text-[11.5px] font-medium text-zinc-400">
            {localizeTemplateName(draft.template_name, t)}
          </p>
        )}
      </div>
      {canDelete && (
        <button
          type="button"
          data-testid={`draft-delete-${draft.id}`}
          onClick={onDelete}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-zinc-400 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/30"
          aria-label={t("common.delete")}
        >
          <Trash2 className="h-4 w-4" strokeWidth={2} />
        </button>
      )}
      {draft.status === "approved" && (
        <Check className="h-4 w-4 shrink-0 text-emerald-500" strokeWidth={2.5} />
      )}
    </div>
  );
}

// Helper: large CTA used elsewhere (e.g. PortalSchedule)
export function DraftTriggerButton({ onClick, testId = "open-draft-picker" }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-full bg-blue-500 px-3.5 py-1.5 text-[12.5px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.5)] transition hover:bg-blue-600"
    >
      <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
      {t("drafts.open_picker")}
    </button>
  );
}

function TemplatesLegend({ templates }) {
  const { t } = useTranslation();
  return (
    <section
      data-testid="templates-legend"
      className="rounded-2xl bg-white p-4 shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900"
    >
      <p className="px-1 text-[11.5px] font-medium uppercase tracking-[0.14em] text-zinc-400">
        {t("drafts.available_templates")}
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {templates.map((tpl) => (
          <span
            key={tpl.id}
            data-testid={`legend-tpl-${tpl.id}`}
            className={
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium ring-1 ring-inset " +
              (COLOR_CHIP[tpl.color] || COLOR_CHIP.indigo)
            }
          >
            <span className={"h-1.5 w-1.5 rounded-full " + (COLOR_DOT[tpl.color] || COLOR_DOT.indigo)} />
            {localizeTemplateName(tpl.name, t)}
            <span className="font-mono text-[10.5px] tabular-nums opacity-70">
              {tpl.start_time}–{tpl.end_time}
            </span>
          </span>
        ))}
        {/* Szabadnap chip — mindig elérhető */}
        <span
          data-testid="legend-day-off"
          className={"inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium ring-1 ring-inset " + COLOR_CHIP.slate}
        >
          <Moon className="h-2.5 w-2.5" strokeWidth={2.5} />
          {t("drafts.day_off")}
        </span>
      </div>
    </section>
  );
}
